import { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
  Linking,
  TextInput,
} from "react-native";
import { useRouter } from "expo-router";
import { useAuth } from "../AuthContext";
import { useAcquisition } from "../AcquisitionContext";
import { SafeAreaView } from "react-native-safe-area-context";
import { useDevice, useScreenDimensions } from "../../_layout";
import { useTranslation } from "react-i18next";
import { Ionicons } from "@expo/vector-icons";
import LoginLeftSidebar from "../components/LoginLeftSidebar";
import AccessWithGoogle from "../components/AccessWithGoogle";
import LanguagePicker from "../components/LanguagePicker";
import CustomActivityIndicator from "../../Components/CustomActivityIndicator";
import NoTranslateText from "../../Components/NoTranslateText";
import BlackPng from "../../../assets/BlackPng.png";
import Colors from "../../../Resources/Color";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "../../Firestore/FirestoreConfig";

/**
 * LoginView view component
 * Web-only login page with gradient sidebar and main content area
 */
const LoginView = () => {
  const [isRestrictedBrowser, setIsRestrictedBrowser] = useState(false);

  const { login } = useAuth();
  const { buildUrlWithParams } = useAcquisition();
  const router = useRouter();
  const { screenSize } = useScreenDimensions();
  const { t, i18n } = useTranslation();
  const { isMobile } = useDevice();

  // Form state - lifted to parent to persist across screenSize changes
  const [signUpHovered, setSignUpHovered] = useState(false);
  const [loginButtonHovered, setLoginButtonHovered] = useState(false);
  const [forgotPasswordHovered, setForgotPasswordHovered] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Detect if user is on restricted in-app browsers where Google Sign-In doesn't work
  useEffect(() => {
    if (typeof navigator !== "undefined") {
      const userAgent = navigator.userAgent;
      const restricted =
        userAgent.includes("Instagram") || // Instagram in-app browser
        userAgent.includes("FBAN") || // Facebook App (iOS)
        userAgent.includes("FBAV") || // Facebook App (Android)
        userAgent.includes("FB_IAB") || // Facebook In-App Browser
        userAgent.includes("musical_ly") || // TikTok
        userAgent.includes("Snapchat") || // Snapchat in-app browser
        userAgent.includes("Line") || // LINE in-app browser
        userAgent.includes("LinkedIn") || // LinkedIn in-app browser
        userAgent.includes("Twitter") || // Twitter in-app browser (X)
        userAgent.includes("Telegram") || // Telegram in-app browser
        userAgent.includes("WhatsApp") || // WhatsApp in-app browser
        userAgent.includes("Pinterest") || // Pinterest in-app browser
        userAgent.includes("Reddit") || // Reddit in-app browser
        userAgent.includes("Cursor"); // Cursor built-in browser
      setIsRestrictedBrowser(restricted);
    }
  }, []);

  // Google sign in handler
  const [googleLoading, setGoogleLoading] = useState(false);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    setGoogleLoading(true);

    await signInWithPopup(auth, provider)
      .then((result) => {
        const credential = GoogleAuthProvider.credentialFromResult(result);
        const token = credential.accessToken;
        const user = result.user;
        setGoogleLoading(false);
      })
      .catch((error) => {
        console.error("Google Sign-in Error:", error);

        const errorCode = error.code;
        const errorMessage = error.message;
        const email = error.customData?.email || "No email available"; // Safe access
        const credential = GoogleAuthProvider.credentialFromError(error);

        console.log("Error details:", {
          errorCode,
          errorMessage,
          email,
          credential,
        });
        setGoogleLoading(false);
      });
  };

  // Terms and Conditions component
  const TermsAndConditions = () => {
    const [termsHovered, setTermsHovered] = useState(false);
    const [privacyHovered, setPrivacyHovered] = useState(false);

    return (
      <>
        <Text style={styles.termsText}>
          {t("byContinuingYouAgree-string")}{" "}
        </Text>
        <TouchableOpacity
          onPress={() =>
            Linking.openURL(
              i18n.language === "es"
                ? "https://www.efortcoach.com/es/terms-and-conditions"
                : "https://www.efortcoach.com/terms-and-conditions"
            ).catch((err) => console.error("Couldn't load page", err))
          }
          onMouseEnter={() => !isMobile && setTermsHovered(true)}
          onMouseLeave={() => setTermsHovered(false)}
        >
          <Text
            style={[
              styles.termsLink,
              termsHovered && { color: "rgba(138, 138, 138, 1)" },
            ]}
          >
            {t("termsAndConditions-string")}
          </Text>
        </TouchableOpacity>
        <Text style={styles.termsText}> {t("and-string")} </Text>
        <TouchableOpacity
          onPress={() =>
            Linking.openURL(
              i18n.language === "es"
                ? "https://www.efortcoach.com/es/privacy-policy"
                : "https://www.efortcoach.com/privacy-policy"
            ).catch((err) => console.error("Couldn't load page", err))
          }
          onMouseEnter={() => !isMobile && setPrivacyHovered(true)}
          onMouseLeave={() => setPrivacyHovered(false)}
        >
          <Text
            style={[
              styles.termsLink,
              privacyHovered && { color: "rgba(138, 138, 138, 1)" },
            ]}
          >
            {t("privacyPolicy-string")}
          </Text>
        </TouchableOpacity>
      </>
    );
  };

  // Email validation regex
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleForgotPassword = () => {
    router.push(buildUrlWithParams("/login/forgot-password"));
  };

  const handleLogin = () => {
    setError(""); // Clear previous errors

    const trimmedEmail = email.trim();

    // Validate email format
    if (trimmedEmail === "") {
      setError(t("emailRequired-string") || "Email is required");
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError(t("invalidEmail-string") || "Invalid email format");
      return;
    }

    // Validate password
    if (password.length < 6) {
      setError(
        t("passwordMinLength-string") ||
          "Password must be at least 6 characters"
      );
      return;
    }

    setIsLoading(true);

    signInWithEmailAndPassword(auth, trimmedEmail, password)
      .then((userCredential) => {
        const user = userCredential.user;
        console.log("User logged in:", user.email);
        setError(""); // Clear error on success
        setIsLoading(false);
      })
      .catch((error) => {
        // Parse Firebase error messages
        let errorMessage = error.message;
        if (error.code === "auth/user-not-found") {
          errorMessage = t("userNotFound-string") || "User not found";
        } else if (error.code === "auth/wrong-password") {
          errorMessage = t("wrongPassword-string") || "Incorrect password";
        } else if (error.code === "auth/invalid-email") {
          errorMessage = t("invalidEmail-string") || "Invalid email format";
        } else if (error.code === "auth/invalid-credential") {
          errorMessage =
            t("invalidCredentials-string") || "Invalid email or password";
        }
        setError(errorMessage);
        setIsLoading(false);
        console.error("Login error:", error);
      });
  };

  // Main content area (right side)
  const MainContent = useMemo(() => {
    return (
      <View style={styles.mainContent}>
        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.contentContainer}>
            {/* Logo */}
            <Image source={BlackPng} style={styles.logo} resizeMode="contain" />

            {/* Heading */}
            <Text style={styles.headingText}>
              {t("welcomeBack-string") || "Welcome back"}
            </Text>

            {/* Show email/password fields for restricted browsers, Google button otherwise */}
            {isRestrictedBrowser ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleLogin();
                }}
                style={{ width: 280 }}
              >
                <View style={styles.emailPasswordContainer}>
                  {/* Email Label */}
                  <Text style={[styles.inputLabel, { marginBottom: -8 }]}>
                    {t("email-string") || "Email"}
                  </Text>

                  <TextInput
                    style={[
                      styles.input,
                      emailFocused && styles.inputFocused,
                      screenSize === "small" && { fontSize: 16 },
                    ]}
                    placeholderTextColor={Colors.grayColor}
                    value={email}
                    onChangeText={(text) => {
                      setError("");
                      setEmail(text);
                    }}
                    onFocus={() => setEmailFocused(true)}
                    onBlur={() => setEmailFocused(false)}
                    onSubmitEditing={() => {
                      // Focus on password field when Enter is pressed
                      if (email) {
                        // Move to next field - password will be focused by default
                      }
                    }}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoComplete="email"
                    maxLength={254}
                    returnKeyType="next"
                  />

                  {/* Password Label with Forgot Password link */}
                  <View style={styles.passwordLabelContainer}>
                    <Text style={styles.inputLabel}>
                      {t("password-string") || "Password"}
                    </Text>
                    <TouchableOpacity
                      onPress={handleForgotPassword}
                      onMouseEnter={() =>
                        !isMobile && setForgotPasswordHovered(true)
                      }
                      onMouseLeave={() => setForgotPasswordHovered(false)}
                      style={{ alignItems: "flex-end" }}
                    >
                      <Text
                        style={[
                          styles.forgotPasswordLink,
                          forgotPasswordHovered &&
                            styles.forgotPasswordLinkHovered,
                        ]}
                      >
                        {t("forgotPassword-string") || "Forgot password?"}
                      </Text>
                    </TouchableOpacity>
                  </View>

                  <View style={styles.passwordContainer}>
                    <TextInput
                      style={[
                        styles.input,
                        passwordFocused && styles.inputFocused,
                        screenSize === "small" && { fontSize: 16 },
                      ]}
                      placeholderTextColor={Colors.grayColor}
                      value={password}
                      onChangeText={(text) => {
                        setError("");
                        setPassword(text);
                      }}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      onSubmitEditing={() => {
                        // Trigger login when Enter is pressed in password field
                        if (email && password.length >= 6 && !isLoading) {
                          handleLogin();
                        }
                      }}
                      secureTextEntry={!showPassword}
                      autoCapitalize="none"
                      autoComplete="password"
                      maxLength={128}
                      returnKeyType="go"
                    />
                    <TouchableOpacity
                      style={styles.showPasswordButton}
                      onPress={() => setShowPassword(!showPassword)}
                      onMouseEnter={(e) => {
                        if (!isMobile) e.currentTarget.style.opacity = "0.7";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = "1";
                      }}
                    >
                      <Ionicons
                        name={showPassword ? "eye-off-outline" : "eye-outline"}
                        size={20}
                        color={Colors.grayColor}
                      />
                    </TouchableOpacity>
                  </View>

                  {/* Error message */}
                  {error != "" && <Text style={styles.errorText}>{error}</Text>}

                  <TouchableOpacity
                    style={[
                      styles.signupButton,
                      (!email || password.length < 6) &&
                        styles.signupButtonDisabled,
                      loginButtonHovered && styles.signupButtonHovered,
                    ]}
                    onPress={handleLogin}
                    onMouseEnter={() =>
                      !isMobile && setLoginButtonHovered(true)
                    }
                    onMouseLeave={() => setLoginButtonHovered(false)}
                    disabled={!email || password.length < 6 || isLoading}
                  >
                    {isLoading ? (
                      <CustomActivityIndicator large={false} white={true} />
                    ) : (
                      <Text style={styles.signupButtonText}>
                        {t("signIn-string") || "Sign In"}
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              </form>
            ) : (
              <AccessWithGoogle
                mode="signin"
                language={i18n.language}
                onPress={signInWithGoogle}
                width={280}
                isLoading={googleLoading}
                setIsLoading={setGoogleLoading}
              />
            )}

            {/* Sign up link */}
            <View style={styles.signInContainer}>
              <NoTranslateText style={styles.signInText}>
                {t("newToEfortCoach-string") || "New to Efort Coach?"}{" "}
              </NoTranslateText>
              <TouchableOpacity
                onPress={() => router.replace(buildUrlWithParams("/signup"))}
                onMouseEnter={() => !isMobile && setSignUpHovered(true)}
                onMouseLeave={() => setSignUpHovered(false)}
              >
                <Text
                  style={[
                    styles.signInLink,
                    signUpHovered && { color: "#2E5E6E" },
                  ]}
                >
                  {t("signUp-string") || "Sign Up"}
                </Text>
              </TouchableOpacity>
            </View>

            {/* Terms and conditions - only show on small screens */}
            {screenSize != "large" && (
              <View style={styles.termsContainerInline}>
                <TermsAndConditions />
              </View>
            )}
          </View>
        </ScrollView>

        {/* Terms and conditions - absolute positioned for larger screens */}
        {screenSize === "large" && (
          <View style={styles.termsContainer}>
            <View style={styles.termsInnerContainer}>
              <TermsAndConditions />
            </View>
          </View>
        )}

        {/* Language Picker - bottom left */}
        <View style={styles.languagePickerContainer}>
          <LanguagePicker />
        </View>
      </View>
    );
  }, [
    screenSize,
    t,
    i18n.language,
    isRestrictedBrowser,
    googleLoading,
    email,
    password,
    emailFocused,
    passwordFocused,
    error,
    isLoading,
    signUpHovered,
    loginButtonHovered,
    forgotPasswordHovered,
    showPassword,
  ]);

  return (
    <SafeAreaView style={styles.container} edges={["top", "bottom"]}>
      {screenSize === "large" && <LoginLeftSidebar />}
      {MainContent}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: "#FFFFFF",
    height: "100%",
  },
  mainContent: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  contentContainer: {
    alignItems: "center",
    maxWidth: "100%",
  },
  logo: {
    width: 48,
    height: 48,
    marginBottom: 32,
  },
  headingText: {
    fontSize: 32,
    fontWeight: "600",
    color: "#000000",
    marginBottom: 40,
    textAlign: "center",
  },
  subtext: {
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    marginBottom: 40,
    textAlign: "center",
  },
  signInContainer: {
    flexDirection: "row",
    marginTop: 16,
    alignItems: "center",
  },
  signInText: {
    fontSize: 14,
    color: Colors.grayColor,
  },
  signInLink: {
    fontSize: 14,
    color: Colors.bubbleColor,
  },
  termsContainer: {
    position: "absolute",
    bottom: 64,
    left: 0,
    right: 0,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  termsInnerContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    maxWidth: 320,
  },
  termsContainerInline: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 40,
    justifyContent: "center",
    alignItems: "center",
    maxWidth: 320,
  },
  termsText: {
    fontSize: 14,
    color: Colors.grayColor,
    textAlign: "center",
  },
  termsLink: {
    fontSize: 14,
    color: Colors.grayColor,
    fontWeight: "400",
    textDecorationLine: "underline",
  },
  languagePickerContainer: {
    position: "absolute",
    bottom: 12,
    left: 12,
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    borderRadius: 10,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  emailPasswordContainer: {
    width: 280,
    gap: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  passwordLabelContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: -8,
  },
  forgotPasswordLink: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  forgotPasswordLinkHovered: {
    color: "rgba(138, 138, 138, 1)",
  },
  passwordContainer: {
    position: "relative",
    width: "100%",
  },
  showPasswordButton: {
    position: "absolute",
    right: 12,
    top: 0,
    bottom: 0,
    paddingHorizontal: 4,
    paddingVertical: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  input: {
    width: "100%",
    height: 44,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: Colors.borderColor,
    borderRadius: 14,
    paddingHorizontal: 16,
    fontSize: 14,
    color: Colors.blackColor,
    outlineStyle: "none",
  },
  inputFocused: {
    borderColor: Colors.bubbleColor,
  },
  passwordCheckmark: {
    position: "absolute",
    right: 16,
    top: 12,
    fontSize: 18,
    color: Colors.bubbleColor,
    fontWeight: "bold",
  },
  errorText: {
    fontSize: 13,
    color: Colors.redColor,
    textAlign: "center",
    marginTop: -8,
  },
  successText: {
    fontSize: 13,
    color: Colors.bubbleColor,
    textAlign: "center",
    marginTop: -8,
  },
  signupButton: {
    width: "100%",
    height: 44,
    backgroundColor: Colors.bubbleColor,
    borderRadius: 14,
    justifyContent: "center",
    alignItems: "center",
  },
  signupButtonDisabled: {
    opacity: 0.5,
  },
  signupButtonHovered: {
    // opacity: 0.8,
    backgroundColor: "#2E5E6E",
  },
  signupButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#FFFFFF",
  },
  fallbackContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
});

export default LoginView;
